/**
 * @section 4.2.1.3
 */
import { CircularProgress, Theme } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import ClearIcon from "@material-ui/icons/Clear";
import SearchIcon from "@material-ui/icons/Search";
import { ICardElementWithChildren } from "backend/model/model";
import { t } from "i18next";
import { getFileApiURL } from "qpr-base/utils/url-utils";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import React, { useState } from "react";
import ScrollArea from "react-scrollbars-custom";
import { useDebounce } from "react-use";
const useStyles = makeThemedStyles("SearchBar", (theme: Theme) => {
  return {
    iconButton: {
      width: "20px",
      height: "20px",
      marginLeft: "15px",
      marginRight: "15px",
      "& span img": {
        width: "1.33em",
        height: "1.33em",
      },
    },
    searchComponent: {
      marginTop: "0px",
      paddingTop: "0px",
      float: "left",
    },
    searchBar: {
      animation: "$expand 0.5s",
      animationIterationCount: 1,
      backgroundColor: "#FAFAFA",
      color: "black",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "39.2em",
      height: "4.94em",
      borderRadius: "2.28em",
      caretColor: "rgba(0, 169, 163, 1)",
      /*
      borderTopLeftRadius: '30px 50%',
      borderBottomLeftRadius: '30px 50%',
      borderTopRightRadius: '30px 50%',
      borderBottomRightRadius: '30px 50%'
      */
    },
    closingSearchBar: {
      animation: "$shrink 0.2s",
      animationIterationCount: 1,
      backgroundColor: "#FAFAFA",
      color: "black",
      display: "flex",
      alignItems: "center",
      right: "100px",
      left: "100px",
      justifyContent: "space-between",
      borderTopLeftRadius: "30px 50%",
      borderBottomLeftRadius: "30px 50%",
      borderTopRightRadius: "30px 50%",
      borderBottomRightRadius: "30px 50%",
      visibility: "hidden",
    },
    typing: {
      outline: "1px solid #29baa4",
    },
    searchInput: {
      marginLeft: "1.17em",
      marginRight: "1.17em",
      borderRight: "none",
      width: "100%",
      border: "none",
      backgroundColor: "rgba(0,0,0,0)",
      outline: "none",
    },
    resultsContainer: {
      position: "absolute",
      width: "39.16em",
      marginTop: "25px",
      backgroundColor: "#FAFAFA",
      boxShadow: "0px 0px 4px lightgray",
      borderRadius: "25px",
      overflow: "hidden",
      overflowY: "auto",
      "&::-webkit-scrollbar": { display: "none" },
    },
    results: {
      margin: "2em",
      marginLeft: "2.1em",
      marginRight: "30px",
    },
    resultTitle: {
      marginTop: "0px",
      color: "gray",
      fontSize: "1.4857em",
      lineHeight: "2.31em",
      fontWeight: 500,
      marginBottom: "0px",
    },
    linkContainer: {
      marginBottom: "2.4em",
      display: "block",
    },
    resultName: {
      color: "rgb(17,94,103)",
      fontSize: "1.6em",
      lineHeight: "2.14em",
      margin: "0.571em 0px",
    },
    clearIcon: {
      margin: "0",
      marginLeft: "16px",
      marginRight: "16px",
      position: "relative",
      color: "rgba(137, 142, 142, 1)",
      cursor: "pointer",
    },
    iconContainer: {
      margin: "0",
      marginLeft: "8px",
      marginRight: "8px",
      position: "relative",
      width: "36px",
      height: "36px",
      display: "flex",
      alignItems: "center",
      backgroundColor: "rgb(6, 156, 240)",
      borderRadius: "50%",
      cursor: "pointer",
    },
    searchIcon: {
      color: "white",
      backgroundColor: "rgb(6, 156, 240)",
      marginLeft: `8px`,
      marginRight: "8px",
      borderRadius: "50%",
    },
    resultRow: {
      display: "flex",
      flexDirection: "row",
      gap: "8px",
      color: "black",
      cursor: "pointer",
      padding: "2px",
    },
    "@keyframes expand": {
      from: {
        transform: "scale(0,1)",
      },
      to: {
        transform: "scale(1,1)",
      },
    },
    "@keyframes shrink": {
      from: {
        transform: "scale(1,1)",
        visibility: "visible",
        width: "450px",
        height: "50px",
      },
      to: {
        transform: "scale(0,1)",
        visibility: "hidden",
        width: "450px",
        height: "50px",
      },
    },
  };
});

const SearchBarDesign = (props: {
  onSearch?: (value: string) => void;
  onNavigate?: (value: ICardElementWithChildren) => void;
  RenderRow: (props: {
    value: ICardElementWithChildren;
    onNavigate?: (value: ICardElementWithChildren) => void;
  }) => JSX.Element;
  results?: ICardElementWithChildren[];
  maxResults?: number;
  isLoading?: boolean;
}) => {
  const maxResults = props.maxResults || 10;
  const classes = useStyles();
  const [showBar, setShowBar] = useState(false);
  const [firstShow, setFirstShow] = useState(true);
  const [search, setSearch] = useState("");
  const [rawInputForSearch, setRawInputForSearch] = useState("");
  const [focusedBar, setFocusedBar] = useState(false);
  const [focusedSearch, setFocusedSearch] = useState(false);
  const [, cancel] = useDebounce(
    () => {
      setSearch(rawInputForSearch);
    },
    500,
    [rawInputForSearch]
  );

  React.useEffect(() => {
    if (props.onSearch) props.onSearch(search);
  }, [search]);

  const handleSearch = () => {};
  const RenderRow = props.RenderRow;

  return (
    <>
      {showBar ? (
        <div className={classes.searchComponent}>
          <div
            className={`${classes.searchBar} ${
              focusedBar || search.length > 0 ? classes.typing : ""
            }`}
            onFocus={() => setFocusedBar(true)}
            onBlur={() => setFocusedBar(false)}
          >
            <input
              placeholder={t("Search")}
              className={classes.searchInput}
              onChange={(e) => setRawInputForSearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  /** Handle Search */
                }
              }}
            />
            {search.length < 3 ? (
              <ClearIcon
                fontSize="large"
                className={classes.clearIcon}
                onClick={() => setShowBar(false)}
                onKeyDown={() => setShowBar(false)}
                aria-hidden="true"
              />
            ) : (
              <div className={classes.iconContainer}>
                <SearchIcon
                  fontSize="large"
                  className={classes.searchIcon}
                  onClick={() => handleSearch()}
                  onKeyDown={() => handleSearch()}
                  aria-hidden="true"
                />
              </div>
            )}
          </div>
          {props?.results?.length ? (
            <div
              className={classes.resultsContainer}
              style={{
                visibility: focusedBar || focusedSearch ? "visible" : "hidden",
              }}
              onMouseEnter={() => setFocusedSearch(true)}
              onMouseLeave={() => setFocusedSearch(false)}
            >
              <div className={classes.results} style={{}}>
                {props.isLoading ? <CircularProgress /> : null}
                {props.results ? (
                  <ScrollArea
                    style={{
                      width: "310px",
                      height: `${
                        4 *
                        (props.results.length < 20 ? props.results.length : 20)
                      }em`,
                    }}
                  >
                    <div>
                      {props.results.slice(0, maxResults).map((value) => {
                        return (
                          <RenderRow
                            key={value.id}
                            value={value}
                            onNavigate={props.onNavigate}
                          />
                        );
                      })}
                    </div>
                  </ScrollArea>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          {!firstShow && <div className={classes.closingSearchBar} />}
          <IconButton
            onClick={() => {
              setShowBar(!showBar);
              setFirstShow(false);
            }}
            onKeyDown={() => {
              setShowBar(!showBar);
              setFirstShow(false);
            }}
            aria-hidden="true"
            className={classes.iconButton}
          >
            <img src={getFileApiURL("/icons/Search.svg")} alt="Search" />
          </IconButton>
        </>
      )}
    </>
  );
};
export default SearchBarDesign;
